import React, { useEffect, useCallback, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import ViewProfileButton from '../../components/candidates/ViewProfileButton';
import ContactButton from '../../components/candidates/ContactButton';
import EmployerHeaderNav from '../../components/navigation/EmployerHeaderNav';
import CandidateSearchCardSkeleton from '../../components/candidate-search/CandidateSearchCardSkeleton';
import supabase from '../../utils/supabase.js';
import { createScrollListener } from '../../utils/scrollUtils';
import { calculateSkillsMatch } from '../../utils/skillsUtils';
import { useCandidateSearch } from './hooks/useCandidateSearch';
import useCandidateAnalytics from '../../hooks/useCandidateAnalytics';

// Import components
import CandidateCard from './components/CandidateCard';
import SearchFilters from './components/SearchFilters';
import CandidateGrid from './components/CandidateGrid';
import LoadingState from './components/LoadingState';

const CandidateSearch = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const analytics = useCandidateAnalytics();
  const [skillSearchQuery, setSkillSearchQuery] = useState('');
  
  // Get all candidate search functionality from our custom hook
  const {
    // State
    loading,
    loadingMore,
    initialLoading,
    searchError,
    searchQuery,
    filters,
    candidates,
    totalCandidates,
    currentPage,
    hasNextPage,
    savedCandidates,
    viewMode,
    showFilters,
    showSavedOnly,
    
    // Actions
    setSearchQuery,
    setViewMode,
    setShowFilters,
    toggleSaveCandidate,
    handleFilterChange,
    handleSkillToggle,
    goToPage,
    clearAllFilters,
    toggleSavedOnly,
    
    // Constants
    skillOptions,
    EXPERIENCE_LEVELS,
    AVAILABILITY_OPTIONS
  } = useCandidateSearch();
  
  // Ensure employer access
  useEffect(() => {
    if (userProfile && userProfile.role !== 'employer' && userProfile.role !== 'admin') {
      navigate('/job-search');
    }
  }, [userProfile, navigate]);
  
  // Handler for viewing a candidate profile
  const handleViewProfile = useCallback((candidateId) => {
    analytics.trackProfileView(candidateId);
    navigate(`/va-profile/${candidateId}`);
  }, [navigate, analytics]);
  
  // Handler for saving/unsaving a candidate
  const handleSaveCandidate = useCallback((candidateId, isSaved) => {
    toggleSaveCandidate(candidateId);
    analytics.trackSaveCandidate(candidateId, !isSaved);
  }, [toggleSaveCandidate, analytics]);
  
  // Handler for contacting a candidate
  const handleContactCandidate = useCallback((candidateId) => {
    analytics.trackContactCandidate(candidateId);
  }, [analytics]);
  
  // Handler for toggling view mode
  const handleViewModeChange = useCallback((mode) => {
    setViewMode(mode);
    analytics.trackViewModeChange(mode);
  }, [setViewMode, analytics]);
  
  // Handler for toggling saved only view
  const handleToggleSavedOnly = useCallback(() => {
    toggleSavedOnly();
  }, [toggleSavedOnly]);
  
  // Handler for toggling filters visibility
  const handleToggleFilters = useCallback(() => {
    setShowFilters(prev => !prev);
  }, [setShowFilters]);
  
  // Handler for clearing all filters
  const handleClearFilters = useCallback(() => {
    clearAllFilters();
    analytics.trackClearFilters();
  }, [clearAllFilters, analytics]);

  return (
    <div className="bg-gray-50 min-h-screen">
      <Helmet>
        <title>Find Virtual Assistants | VA Sprouts</title>
        <meta name="description" content="Search for skilled virtual assistants on VA Sprouts. Filter by skills, experience, and more to find the perfect VA for your business needs." />
      </Helmet>
      
      <EmployerHeaderNav activeTab="candidates" />
      
      <div className="content-container mx-auto px-4 py-8">
        <div className="flex flex-col space-y-6">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <h1 className="text-2xl font-bold text-gray-900">Find Virtual Assistants</h1>
            
            <div className="flex items-center mt-4 md:mt-0 space-x-4">
              <button
                onClick={handleToggleFilters}
                className="text-sm flex items-center text-blue-600 hover:text-blue-800"
              >
                <span>{showFilters ? 'Hide Filters' : 'Show Filters'}</span>
                <Icon
                  name={showFilters ? 'chevron-up' : 'chevron-down'}
                  className="ml-1 h-4 w-4"
                />
              </button>
              
              <div className="flex items-center space-x-1">
                <Button
                  variant={showSavedOnly ? 'primary' : 'ghost'}
                  size="sm"
                  onClick={handleToggleSavedOnly}
                  iconName="Heart"
                  className="mr-2"
                >
                  {showSavedOnly ? 'All' : 'Favorites'}
                </Button>
                <div className="hidden md:flex items-center space-x-1">
                  <Button
                        variant={viewMode === 'grid' ? 'primary' : 'ghost'}
                        size="sm"
                        onClick={() => handleViewModeChange('grid')}
                        iconName="Grid3X3"
                      />
                      <Button
                        variant={viewMode === 'list' ? 'primary' : 'ghost'}
                        size="sm"
                        onClick={() => handleViewModeChange('list')}
                        iconName="List"
                      />
                </div>
              </div>
            </div>
          </div>
          
          {/* Two-column layout with left sidebar for filters */}
          <div className="flex flex-col md:flex-row gap-6">
            {/* Filters Sidebar */}
            <div className={`md:w-1/4 ${showFilters ? 'block' : 'hidden md:block'}`}>
              <div className="bg-white rounded-lg shadow p-4 sticky top-36">
                {/* Search Input */}
                <div className="mb-4">
                  <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
                    Search
                  </label>
                  <div className="relative">
                    <Input
                      id="search"
                      type="text"
                      placeholder="Search candidates..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 pr-4"
                      fullWidth
                    />
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Icon name="search" size={18} className="text-gray-400" />
                    </div>
                  </div>
                </div>
                
                {/* Toggle Filters */}
                <div className="md:hidden mb-4">
                  <Button
                    onClick={handleToggleFilters}
                    variant="outline"
                    fullWidth
                  >
                    {showFilters ? 'Hide Filters' : 'Show Filters'}
                    <Icon
                      name={showFilters ? 'chevron-up' : 'chevron-down'}
                      className="ml-2 h-4 w-4"
                    />
                  </Button>
                </div>
                
                {/* Filters */}
                {showFilters && (
                  <div className="space-y-4">
                    {/* Skills Filter */}
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        Skills
                      </label>
                      <div className="mb-3">
                        <div className="relative">
                          <Input
                            type="text"
                            placeholder="Search skills..."
                            value={skillSearchQuery}
                            onChange={(e) => setSkillSearchQuery(e.target.value.toLowerCase())}
                            className="pl-10 pr-4"
                            fullWidth
                          />
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Icon name="search" size={18} className="text-gray-400" />
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 max-h-36 overflow-y-auto">
                        {skillOptions
                          .filter((skill) => skill.toLowerCase().includes(skillSearchQuery))
                          .map((skill) => (
                            <button
                              key={skill}
                              onClick={() => handleSkillToggle(skill)}
                              className={`px-2 py-1 text-xs rounded-full border transition-colors ${
                                filters.skills.includes(skill)
                                  ? 'bg-primary-100 border-primary-300 text-primary-700'
                                  : 'bg-surface border-border text-text-secondary hover:border-primary-300'
                              }`}
                            >
                              {skill}
                            </button>
                          ))}
                      </div>
                      {skillSearchQuery && skillOptions.filter((skill) => skill.toLowerCase().includes(skillSearchQuery)).length === 0 && (
                        <p className="text-xs text-gray-500 mt-2">No skills match "{skillSearchQuery}"</p>
                      )}
                    </div>
                    
                    {/* Experience Level */}
                    <div>
                      <label htmlFor="experience_level" className="block text-sm font-medium text-gray-700 mb-1">
                        Experience Level
                      </label>
                      <select
                        id="experience_level"
                        value={filters.experience_level}
                        onChange={(e) => handleFilterChange('experience_level', e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      >
                        <option value="">Any Experience</option>
                        {EXPERIENCE_LEVELS.map((level) => (
                          <option key={level.value} value={level.value}>
                            {level.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    {/* Availability */}
                    <div>
                      <label htmlFor="availability" className="block text-sm font-medium text-gray-700 mb-1">
                        Availability
                      </label>
                      <select
                        id="availability"
                        value={filters.availability}
                        onChange={(e) => handleFilterChange('availability', e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      >
                        <option value="">Any Availability</option>
                        {AVAILABILITY_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    {/* Hourly Rate Range */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Hourly Rate
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Input
                            type="number"
                            placeholder="Min $"
                            value={filters.hourly_rate_min}
                            onChange={(e) => handleFilterChange('hourly_rate_min', e.target.value)}
                            min="0"
                            className="text-sm"
                          />
                        </div>
                        <div>
                          <Input
                            type="number"
                            placeholder="Max $"
                            value={filters.hourly_rate_max}
                            onChange={(e) => handleFilterChange('hourly_rate_max', e.target.value)}
                            min="0"
                            className="text-sm"
                          />
                        </div>
                      </div>
                    </div>
                    
                    {/* Location */}
                    {/* <div>
                      <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-1">
                        Location
                      </label>
                      <Input
                        id="location"
                        type="text"
                        placeholder="Enter location"
                        value={filters.location}
                        onChange={(e) => handleFilterChange('location', e.target.value)}
                      />
                    </div> */}
                    
                    {/* Clear Filters */}
                    <div className="pt-2">
                      <Button
                        onClick={handleClearFilters}
                        variant="outline"
                        fullWidth
                      >
                        Clear All Filters
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Main Content Area */}
            <div className="md:flex-1">
              
              {/* Error State */}
              {searchError && (
                <div className="bg-red-50 text-red-800 p-4 rounded-md mb-4">
                  <p>{searchError}</p>
                </div>
              )}
              
              {/* Results */}
              {initialLoading ? (
                <LoadingState viewMode={viewMode} count={6} />
              ) : (
                <div>
                  
                  <div className={`grid ${
                    viewMode === 'grid' 
                      ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-2' 
                      : 'grid-cols-1'
                  } gap-4`}>
                    {candidates.map((candidate) => (
                      <CandidateCard
                        key={candidate.id}
                        candidate={candidate}
                        user={userProfile}
                        savedCandidates={savedCandidates}
                        viewMode={viewMode}
                        onViewProfile={() => handleViewProfile(candidate.id)}
                        onSaveCandidate={() => handleSaveCandidate(candidate.id, savedCandidates.includes(candidate.id))}
                        onContactCandidate={() => handleContactCandidate(candidate.id)}
                      />
                    ))}
                  </div>

                  {/* View mode controls */}
              <div className="mb-4 mt-8">
                {totalCandidates > 0 ? (
                  <div className="text-sm text-gray-500 text-center">
                    <span>
                      {showSavedOnly ? 'Saved: ' : 'Found '}
                      {totalCandidates} candidate{totalCandidates !== 1 ? 's' : ''}
                    </span>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12">
                    <Icon 
                      name={showSavedOnly ? 'Heart' : 'Users'} 
                      size={48} 
                      className="text-gray-300 mb-3"
                    />
                    <span className="text-lg text-gray-500">
                      {showSavedOnly 
                        ? 'No saved candidates' 
                        : 'No candidates found matching your criteria'
                      }
                    </span>
                  </div>
                )}
              </div>
                  
                  {/* Pagination Controls */}
                  {totalCandidates > 0 && (
                    <div className="mt-8 flex items-center justify-center gap-2">
                      <Button
                        onClick={() => goToPage(currentPage - 1)}
                        variant="outline"
                        size="sm"
                        disabled={currentPage === 1 || loading}
                        iconName="ChevronLeft"
                      >
                        Previous
                      </Button>
                      
                      <div className="flex items-center gap-1">
                        {Array.from({ length: Math.ceil(totalCandidates / 12) }).map((_, i) => {
                          const pageNum = i + 1;
                          const isCurrentPage = pageNum === currentPage;
                          
                          // Show page numbers with ellipsis for large pagination
                          if (
                            pageNum === 1 ||
                            pageNum === Math.ceil(totalCandidates / 12) ||
                            (pageNum >= currentPage - 2 && pageNum <= currentPage + 2)
                          ) {
                            return (
                              <Button
                                key={pageNum}
                                onClick={() => goToPage(pageNum)}
                                variant={isCurrentPage ? 'primary' : 'ghost'}
                                size="sm"
                                disabled={loading}
                                className={isCurrentPage ? 'min-w-10' : ''}
                              >
                                {pageNum}
                              </Button>
                            );
                          }
                          
                          if (pageNum === currentPage - 3 || pageNum === currentPage + 3) {
                            return (
                              <span key={`ellipsis-${pageNum}`} className="px-2 text-gray-500">
                                ...
                              </span>
                            );
                          }
                          
                          return null;
                        })}
                      </div>
                      
                      <Button
                        onClick={() => goToPage(currentPage + 1)}
                        variant="outline"
                        size="sm"
                        disabled={!hasNextPage || loading}
                        iconName="ChevronRight"
                      >
                        Next
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CandidateSearch;